import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

const DOWNLOAD_VALUES = [
  'download',
  'download_pdf',
  'downloadPdf',
  'pdf',
  'download_docx',
  'downloadDocx',
  'docx',
];

export const extractPlainTextFromHtml = (html) => {
  const helper = document.createElement('div');
  helper.innerHTML = html;
  return helper.textContent || helper.innerText || '';
};

/** @param {string | null | undefined} s */
function normalizeWhitespaceText(s) {
  return String(s ?? '')
    .replace(/\r\n/g, '\n')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Recorre nodos inline (texto + <strong>/<b>, <em>/<i>, <code>, <br>) y
 * arma los TextRun de docx correspondientes, en vez de aplanar todo a texto
 * plano vía `el.textContent`. Antes de esto, cualquier negrita/cursiva/código
 * dentro de un párrafo, celda o item de lista se perdía en silencio — el
 * .docx quedaba con la estructura correcta (párrafos, tablas, listas) pero
 * sin ningún estilo de caracter, aunque el PDF (que sí es una captura visual
 * del HTML) los mostrara bien. CT-5305: era la brecha de fidelidad que
 * quedaba entre ambos formatos.
 *
 * @param {Node} node
 * @param {typeof import('docx')} mod
 * @param {{ bold?: boolean, italics?: boolean, code?: boolean }} marks
 * @returns {import('docx').TextRun[]}
 */
function inlineNodeToTextRuns(node, mod, marks) {
  const { TextRun } = mod;

  if (node.nodeType === 3) {
    const text = node.textContent.replace(/[\t\n\r]+/g, ' ');
    if (!text) return [];
    return [
      new TextRun({
        text,
        bold: marks.bold || undefined,
        italics: marks.italics || undefined,
        font: marks.code ? 'Courier New' : undefined,
      }),
    ];
  }

  if (node.nodeType !== 1) return [];

  const tag = node.tagName?.toUpperCase();
  if (tag === 'BR') return [new TextRun({ text: '', break: 1 })];

  const nextMarks = { ...marks };
  if (tag === 'STRONG' || tag === 'B') nextMarks.bold = true;
  if (tag === 'EM' || tag === 'I') nextMarks.italics = true;
  if (tag === 'CODE') nextMarks.code = true;

  const runs = [];
  for (const child of node.childNodes) {
    runs.push(...inlineNodeToTextRuns(child, mod, nextMarks));
  }
  return runs;
}

/**
 * @param {Element} el
 * @param {typeof import('docx')} mod
 * @returns {import('docx').TextRun[]} nunca vacío — cae a un TextRun(' ') para que
 *   el párrafo/celda resultante no quede sin `children`.
 */
function inlineChildrenToTextRuns(el, mod) {
  const runs = [];
  for (const child of el.childNodes) {
    runs.push(...inlineNodeToTextRuns(child, mod, {}));
  }
  return runs.length ? runs : [new mod.TextRun(' ')];
}

/**
 * @param {HTMLTableElement} tableEl
 * @param {typeof import('docx')} mod
 */
function buildDocxTableFromHtmlTable(tableEl, mod) {
  const { Paragraph, TextRun, Table, TableRow, TableCell } = mod;
  /** @type {Element[][]} */
  const rowCells = [];
  for (const tr of tableEl.querySelectorAll('tr')) {
    rowCells.push([...tr.querySelectorAll('th, td')]);
  }
  if (!rowCells.length) return null;
  const colCount = Math.max(...rowCells.map((r) => r.length), 1);
  const colW = Math.floor(9000 / colCount);
  const columnWidths = Array.from({ length: colCount }, () => colW);

  const rows = rowCells.map((cells) => {
    const padded = [...cells];
    while (padded.length < colCount) padded.push(null);
    return new TableRow({
      children: padded.map(
        (cell) =>
          new TableCell({
            children: [
              new Paragraph({ children: cell ? inlineChildrenToTextRuns(cell, mod) : [new TextRun(' ')] }),
            ],
          }),
      ),
    });
  });

  return new Table({ columnWidths, rows });
}

/**
 * @param {ParentNode} body
 * @returns {Element[]}
 */
function getTopLevelBlockRoots(body) {
  const kids = [...body.children];
  if (kids.length === 1) {
    const only = kids[0];
    const t = only.tagName?.toUpperCase();
    if (['DIV', 'SECTION', 'ARTICLE', 'MAIN'].includes(t)) {
      return [...only.children];
    }
  }
  return kids;
}

/**
 * @param {Element} el
 * @param {import('docx').FileChild[]} out
 * @param {typeof import('docx')} mod
 */
function appendBlocksFromElement(el, out, mod) {
  const { Paragraph, TextRun } = mod;
  const tag = el.tagName?.toUpperCase();

  if (tag === 'TABLE') {
    const t = buildDocxTableFromHtmlTable(/** @type {HTMLTableElement} */ (el), mod);
    if (t) out.push(t);
    return;
  }

  if (['P', 'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'BLOCKQUOTE'].includes(tag)) {
    if (normalizeWhitespaceText(el.textContent)) {
      out.push(new Paragraph({ children: inlineChildrenToTextRuns(el, mod) }));
    }
    return;
  }

  if (tag === 'UL') {
    for (const li of el.querySelectorAll(':scope > li')) {
      if (!normalizeWhitespaceText(li.textContent)) continue;
      out.push(
        new Paragraph({ children: [new TextRun('• '), ...inlineChildrenToTextRuns(li, mod)] }),
      );
    }
    return;
  }

  if (tag === 'OL') {
    let idx = 1;
    for (const li of el.querySelectorAll(':scope > li')) {
      if (normalizeWhitespaceText(li.textContent)) {
        out.push(
          new Paragraph({ children: [new TextRun(`${idx}. `), ...inlineChildrenToTextRuns(li, mod)] }),
        );
      }
      idx += 1;
    }
    return;
  }

  if (tag === 'HR') {
    out.push(new Paragraph({ children: [new TextRun('—')] }));
    return;
  }

  if (['DIV', 'SECTION', 'ARTICLE', 'MAIN', 'FIGURE'].includes(tag)) {
    for (const child of el.children) {
      appendBlocksFromElement(child, out, mod);
    }
    return;
  }

  if (tag === 'BR') return;

  if (normalizeWhitespaceText(el.textContent)) {
    out.push(new Paragraph({ children: inlineChildrenToTextRuns(el, mod) }));
  }
}

/**
 * HTML (p. ej. salida de TipTap) → hijos de sección docx: párrafos + tablas.
 *
 * Exportada para que cualquier flujo que genere un .docx desde el HTML del
 * chat (descarga directa, "Save to Drive", etc.) construya el mismo
 * documento en vez de reimplementar su propio parseo con distinta fidelidad
 * — ver `chatDocumentSaveToDrive.js` (CT-5305).
 *
 * @param {string} html
 * @param {typeof import('docx')} mod
 * @returns {import('docx').FileChild[]}
 */
export function htmlToDocxSectionChildren(html, mod) {
  const parser = new DOMParser();
  const dom = parser.parseFromString(html, 'text/html');
  const body = dom.body;
  /** @type {import('docx').FileChild[]} */
  const out = [];

  for (const el of getTopLevelBlockRoots(body)) {
    if (el.nodeType === 1) appendBlocksFromElement(/** @type {Element} */ (el), out, mod);
  }

  if (!out.length) {
    const fallback = normalizeWhitespaceText(body.textContent);
    out.push(new mod.Paragraph({ children: [new mod.TextRun(fallback || ' ')] }));
  }

  return out;
}

const renderPlainTextPdf = ({ safeTitle, textContent }) => {
  const normalizedText = textContent
    .replace(/\r\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (!normalizedText) return;

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  });

  pdf.setFontSize(18);
  pdf.setTextColor(0, 0, 0);
  pdf.text(safeTitle.replace(/_/g, ' '), 20, 20);
  pdf.setDrawColor(0, 0, 0);
  pdf.setLineWidth(0.5);
  pdf.line(20, 24, 190, 24);

  pdf.setFontSize(12);
  const paragraphs = normalizedText.split('\n');
  const pageWidth = 170;
  const lineHeight = 7;
  let yPosition = 30;

  paragraphs.forEach((paragraph) => {
    if (!paragraph.trim()) {
      yPosition += lineHeight / 2;
      return;
    }

    if (yPosition > 270) {
      pdf.addPage();
      yPosition = 20;
    }

    const lines = pdf.splitTextToSize(paragraph, pageWidth);
    pdf.text(lines, 20, yPosition);
    yPosition += lines.length * lineHeight + 2;
  });

  pdf.save(`${safeTitle}.pdf`);
};

/**
 * Renderiza HTML a un PDF paginado (instancia de jsPDF) vía html2canvas —
 * misma estrategia que usaba antes solo `downloadDocumentAsPdf`. Exportada
 * (CT-5305) para que "Save to Drive" produzca el mismo PDF que "Download"
 * en vez de uno de texto plano sin ningún formato.
 *
 * Lanza si el render falla (fuentes no cargadas, HTML vacío, etc.) — quien
 * llama debe capturar y recurrir a un PDF de texto plano en ese caso, en
 * vez de devolver un archivo silenciosamente vacío.
 *
 * @param {string} html
 * @returns {Promise<import('jspdf').jsPDF>}
 */
export async function renderHtmlToPdf(html) {
  const printContainer = document.createElement('div');
  printContainer.style.position = 'fixed';
  printContainer.style.left = '0';
  printContainer.style.top = '0';
  printContainer.style.width = '794px';
  printContainer.style.padding = '24px';
  printContainer.style.background = '#ffffff';
  printContainer.style.color = '#111827';
  printContainer.style.fontFamily = 'Inter, Arial, sans-serif';
  printContainer.style.fontSize = '14px';
  printContainer.style.lineHeight = '1.6';
  printContainer.style.zIndex = '-1';
  printContainer.style.pointerEvents = 'none';
  printContainer.innerHTML = html;

  const elements = printContainer.querySelectorAll('*');
  elements.forEach((element) => {
    element.style.color = '#111827';
    element.style.background = 'transparent';
  });

  // The app's Tailwind reset sets `list-style: none` on every <ul>/<ol> —
  // the real editors only get their bullets/numbers back because they scope
  // `list-disc`/`list-decimal` to their own wrapper class (`.ProseMirror`,
  // `.markdown-content`), which this detached container never has. Restoring
  // it explicitly here means the PDF shows real markers no matter which
  // renderer produced the HTML, instead of silently losing every <li>'s
  // bullet/number (CT-5305).
  printContainer.querySelectorAll('ul').forEach((el) => {
    el.style.listStyleType = 'disc';
    el.style.paddingLeft = '1.5em';
  });
  printContainer.querySelectorAll('ol').forEach((el) => {
    el.style.listStyleType = 'decimal';
    el.style.paddingLeft = '1.5em';
  });
  printContainer.querySelectorAll('li').forEach((el) => {
    el.style.display = 'list-item';
  });

  document.body.appendChild(printContainer);

  try {
    if (document.fonts?.ready) {
      await document.fonts.ready;
    }

    const canvas = await html2canvas(printContainer, {
      scale: 2,
      backgroundColor: '#ffffff',
      useCORS: true,
      scrollX: 0,
      scrollY: 0,
      windowWidth: printContainer.scrollWidth,
      windowHeight: printContainer.scrollHeight,
    });

    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'a4',
    });

    const margin = 10;
    const pageWidthMm = pdf.internal.pageSize.getWidth() - margin * 2;
    const pageHeightMm = pdf.internal.pageSize.getHeight() - margin * 2;

    /** Altura en px de cada franja de canvas que ocupa exactamente `pageHeightMm` al escalarse a `pageWidthMm`. */
    const slicePxTarget = (pageHeightMm * canvas.width) / pageWidthMm;

    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = canvas.width;
    const sliceCtx = sliceCanvas.getContext('2d');

    let yPx = 0;
    let firstPage = true;

    while (yPx < canvas.height - 0.5) {
      if (!firstPage) pdf.addPage();
      firstPage = false;

      const slicePx = Math.min(slicePxTarget, canvas.height - yPx);
      const sliceH = Math.max(1, Math.ceil(slicePx));
      sliceCanvas.height = sliceH;
      sliceCtx.fillStyle = '#ffffff';
      sliceCtx.fillRect(0, 0, sliceCanvas.width, sliceH);
      sliceCtx.drawImage(canvas, 0, yPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);

      const sliceData = sliceCanvas.toDataURL('image/png');
      const sliceHeightMm = (slicePx * pageWidthMm) / canvas.width;
      pdf.addImage(sliceData, 'PNG', margin, margin, pageWidthMm, sliceHeightMm);

      yPx += slicePx;
    }

    return pdf;
  } finally {
    document.body.removeChild(printContainer);
  }
}

export const isDownloadMenuAction = (value) => DOWNLOAD_VALUES.includes(value);

const safeFileBaseName = (title) =>
  (typeof title === 'string' ? title : 'Document')
    .replace(/\s+/g, '_')
    .replace(/[^\w.-]/g, '')
    .trim() || 'Document';

export const downloadDocumentAsPdf = async ({ title, content, contentFormat, detectContentFormat }) => {
  const source = typeof content === 'string' ? content : String(content ?? '');
  if (!source.trim()) return;

  const safeTitle = safeFileBaseName(title);

  const format = contentFormat ?? detectContentFormat(source);
  if (format === 'html') {
    const fallbackText = extractPlainTextFromHtml(source);
    if (!fallbackText.trim()) return;

    try {
      const pdf = await renderHtmlToPdf(source);
      pdf.save(`${safeTitle}.pdf`);
    } catch {
      renderPlainTextPdf({ safeTitle, textContent: fallbackText });
    }
    return;
  }

  renderPlainTextPdf({ safeTitle, textContent: source });
};
